import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import {
  ClassificationIcon,
  Grid,
  LoadingIndicator,
  NoData
} from "../../../../../../components";
import { YaraMatch } from "../../../../@types";
import { YaraMatchesGridProps } from "../../@types";
import { convertYaraMatchesToGridData } from "./utils";
import Typography from "../../../../../../components/typography/Typography";
import styles from "./yara-matches.module.scss";
import commonStyles from "../../../../containers/report_summary/report-summary.module.scss";
import { useLocalSampleSummaryParams } from "../../../../hooks";
import { pathToSS } from "../../../../util";
import { SECTIONS } from "../../../../consts";
import YaraMatchesMatchedRules from "./YaraMatchesMatchedRules";
import YaraMatchesMatchedRuleset from "./YaraMatchesMatchedRuleset";

const YaraMatchesErrorComponent = () => (
  <span className={commonStyles.error_msg}>Yara Matches Error</span>
);

const YaraMatchesEmptyGridComponent = () => (
  <span className={commonStyles.error_msg}>No available Yara matches</span>
);

const yaraMatchesSchema = (sourceId?: number) => ({
  fields: [
    {
      name: "matched_ruleset",
      displayTitle: "Matched Ruleset",
      sortable: false,
      component: (data: YaraMatchesGridProps) => (
        <YaraMatchesMatchedRuleset yaraMatch={data} />
      ),
      style: {
        width: "25%"
      }
    },
    {
      name: "matched_rules",
      displayTitle: "Matched Rules",
      sortable: false,
      component: (data: YaraMatchesGridProps) =>
        (
          <YaraMatchesMatchedRules
            rules={data.matchedRules}
            matchedRulesetId={data.matchedRulesetId}
          />
        ) || <NoData />,
      style: {
        width: "45%"
      },
      className: "overflow-ellipsis"
    },
    {
      name: "no_of_matches",
      displayTitle: "No. of Matches",
      sortable: false,
      component: (data: YaraMatchesGridProps) => data.noOfMatches,
      style: {
        width: "15%"
      }
    },
    {
      name: "file",
      displayTitle: "File",
      sortable: false,
      component: (data: YaraMatchesGridProps) => (
        <Link
          to={pathToSS(
            data.fileSha1,
            sourceId,
            SECTIONS.TI_CORE.YARA,
            data.fileRelationshipId
          )}
          className={styles.grid_element_with_classification}
          data-testid="summary_report_yara_grid_file_link"
        >
          <ClassificationIcon
            classification={data.fileClassification}
            classificationStyle={{
              flexShrink: 0
            }}
          />
          <Typography
            variant={"body1"}
            className={`${styles.item_link} overflow-ellipsis`}
          >
            {data.filename}
          </Typography>
        </Link>
      )
    }
  ]
});

interface Props {
  data?: Array<YaraMatch>;
  isLoading?: boolean;
}

const YaraMatchesGrid = ({ data, isLoading }: Props) => {
  const { sourceId } = useLocalSampleSummaryParams();
  const gridData = convertYaraMatchesToGridData(data || []);

  return (
    <ErrorBoundary fallback={<YaraMatchesErrorComponent />}>
      <Grid
        schema={yaraMatchesSchema(sourceId)}
        data={gridData}
        hidePaging
        showLoading={isLoading}
        LoadingComponent={<LoadingIndicator style={{ margin: "10rem 0" }} />}
        emptyGridMsg={<YaraMatchesEmptyGridComponent />}
      />
    </ErrorBoundary>
  );
};

export default YaraMatchesGrid;
